/**
 * Firestore Document Types
 * 
 * Type definitions for all Firestore collections and documents.
 * Updated for tiered usage billing with bucket-based pricing.
 * 
 * @module lib/firebase-types
 */

import type { Timestamp } from 'firebase/firestore';

// =============================================================================
// Plan & Billing Types
// =============================================================================

/** Plan tier identifiers */
export type PlanId = 'free' | 'starter' | 'pro' | 'elite' | 'enterprise';

/** Billing interval */
export type BillingInterval = 'monthly' | 'annual';

// =============================================================================
// Usage Tracking Types
// =============================================================================

/**
 * Per-API usage breakdown for dashboard display
 *
 * Tracks both raw API calls and weighted usage to show customers
 * how their quota is being consumed across different APIs.
 *
 * @example
 * ```typescript
 * {
 *   geo: { rawCalls: 5000, weightedUsage: 5000, weight: 1.0 },
 *   currency: { rawCalls: 2000, weightedUsage: 2000, weight: 1.0 },
 *   'ai-enrichment': { rawCalls: 450, weightedUsage: 1350, weight: 3.0 },
 * }
 * ```
 */
export interface UsageByApi {
  [apiId: string]: {
    /** Actual number of API calls made */
    rawCalls: number;
    /** Weighted usage consumed from quota (rawCalls × weight) */
    weightedUsage: number;
    /** Weight multiplier at time of usage */
    weight: number;
  };
}

// =============================================================================
// Organization Types
// =============================================================================

/**
 * Organization document in Firestore.
 * Path: /organizations/{orgId}
 */
export interface Organization {
  /** Auto-generated document ID */
  id: string;
  
  /** Display name (e.g., "Acme Corp") */
  name: string;
  
  /** URL-safe slug (e.g., "acme-corp"), unique */
  slug: string;
  
  /** Current plan tier */
  planId: PlanId;

  /** Account type (standard or internal for dogfood accounts) */
  accountType?: 'standard' | 'internal';
  
  // =========================================================================
  // Stripe Billing
  // =========================================================================
  
  /** Stripe customer ID (set when billing is configured) */
  stripeCustomerId?: string;
  
  /** Stripe subscription ID (set when subscribed) */
  stripeSubscriptionId?: string;
  
  /** Stripe subscription item ID (for updating prices) */
  stripeSubscriptionItemId?: string;
  
  /** Stripe payment method ID (for auto-upgrades) */
  stripePaymentMethodId?: string;
  
  /** Current Stripe price lookup key (e.g., "starter_15k_monthly") */
  stripePriceLookupKey?: string;
  
  /** Billing interval */
  billingInterval?: BillingInterval;
  
  /** Whether this is the first billing period (for intro pricing) */
  isIntroPeriod?: boolean;
  
  /** Current billing period start */
  currentPeriodStart?: Timestamp;
  
  /** Current billing period end */
  currentPeriodEnd?: Timestamp;
  
  // =========================================================================
  // Bucket Tracking (Tiered Usage Billing)
  // =========================================================================
  
  /** 
   * Current bucket maximum requests
   * e.g., 15000 for starter_15k bucket
   * This determines the current price tier within the plan
   */
  currentBucketMax?: number;
  
  /**
   * Current bucket price in cents
   * e.g., 599 for $5.99/mo
   */
  currentBucketPriceCents?: number;
  
  /**
   * Requests used this billing period (paid plans)
   * Resets at the start of each billing period
   */
  usageThisPeriod: number;
  
  /**
   * Peak usage this billing period
   * Used to determine if bucket upgrade is needed
   */
  peakUsageThisPeriod?: number;

  /**
   * Per-API usage breakdown for dashboard display
   *
   * Shows how quota is consumed across different APIs, including
   * raw call counts and weighted usage for transparency.
   *
   * @example
   * ```typescript
   * {
   *   geo: { rawCalls: 5000, weightedUsage: 5000, weight: 1.0 },
   *   'ai-enrichment': { rawCalls: 450, weightedUsage: 1350, weight: 3.0 },
   * }
   * ```
   */
  usageByApi?: UsageByApi;

  /**
   * Whether a bucket upgrade is pending
   * Set when usage exceeds bucket, cleared when Stripe confirms upgrade
   */
  bucketUpgradePending?: boolean;
  
  /**
   * Timestamp of last bucket upgrade
   * Used to prevent rapid successive upgrades
   */
  lastBucketUpgradeAt?: Timestamp;
  
  // =========================================================================
  // Usage Tracking (Free Plan - Lifetime Bucket)
  // =========================================================================
  
  /** Total lifetime requests used (free plan, never resets) */
  lifetimeUsage: number;
  
  /** Total bonus requests earned (free plan) */
  lifetimeBonusEarned: number;
  
  /** Bonus actions completed */
  bonusActions?: BonusActionsCompleted;
  
  // =========================================================================
  // Grace Period & Rate Limiting
  // =========================================================================
  
  /** When grace period started (after failed payment) */
  graceStartedAt?: Timestamp;
  
  /** Whether API is currently rate limited */
  isRateLimited?: boolean;
  
  /** Whether API is hard stopped (free tier exhausted or payment failed) */
  isHardStopped?: boolean;
  
  // =========================================================================
  // Timestamps
  // =========================================================================
  
  /** When the organization was created */
  createdAt: Timestamp;
  
  /** When the organization was last updated */
  updatedAt: Timestamp;
  
  /** User ID of the creator */
  createdBy: string;
  
  // =========================================================================
  // Members
  // =========================================================================
  
  /** Array of user IDs who are members (for security rules) */
  memberIds: string[];

  /** User ID of the organization owner (single owner) */
  ownerId: string;

  // =========================================================================
  // Team Seat Billing
  // =========================================================================

  /** Number of billable team seats (members - 2, since owner + 1 free invite) */
  billableSeats?: number;

  /** Stripe subscription item ID for seat billing */
  stripeSeatItemId?: string;

  /** Pending billable seats (stored when no subscription exists) */
  pendingBillableSeats?: number;
}

/**
 * Bonus actions completed for free tier gamification
 */
export interface BonusActionsCompleted {
  /** Phone number verified */
  phoneVerified?: boolean;
  /** Payment method added */
  paymentMethodAdded?: boolean;
  /** Profile completed */
  profileCompleted?: boolean;
  /** First API key created */
  firstApiKeyCreated?: boolean;
  /** First API call made */
  firstApiCallMade?: boolean;
}

/**
 * Organization member roles
 *
 * Role hierarchy (1 = lowest, 6 = highest):
 * - owner (6): Full access. Can transfer ownership and delete the organization.
 * - org_admin (5): Full access except transferring ownership or deleting the organization. Can receive ownership.
 * - billing_admin (4): Admin powers plus billing management.
 * - admin (3): Can manage API keys, view usage, and invite/remove admin, manager, and member roles.
 * - manager (2): Can manage API keys, view usage, and invite/remove members only.
 * - member (1): View-only access. Can view API keys and usage statistics.
 */
export type OrgRole = 'owner' | 'org_admin' | 'billing_admin' | 'admin' | 'manager' | 'member';

/**
 * Role level mapping for permission checks
 */
export const ROLE_LEVEL: Record<OrgRole, number> = {
  owner: 6,
  org_admin: 5,
  billing_admin: 4,
  admin: 3,
  manager: 2,
  member: 1,
};

/**
 * Human-readable role labels for display in UI
 */
export const ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  org_admin: 'Org Admin',
  billing_admin: 'Billing Admin',
  admin: 'Admin',
  manager: 'Manager',
  member: 'Member',
};

/**
 * Organization member document.
 * Path: /organizations/{orgId}/members/{userId}
 */
export interface OrganizationMember {
  /** User ID (matches document ID) */
  userId: string;
  
  /** User's email (denormalized for display) */
  email: string;
  
  /** User's display name (denormalized for display) */
  displayName?: string;
  
  /** User's photo URL (denormalized for display) */
  photoUrl?: string;
  
  /** Role within the organization */
  role: OrgRole;
  
  /** When they joined the organization */
  joinedAt: Timestamp;
  
  /** User ID of who invited them (if invited) */
  invitedBy?: string;
}

// =============================================================================
// API Key Types
// =============================================================================

/**
 * API key type
 */
export type ApiKeyType = 'live' | 'test';

/**
 * API key status
 */
export type ApiKeyStatus = 'active' | 'revoked';

/**
 * API key document.
 * Path: /organizations/{orgId}/api_keys/{keyId}
 */
export interface ApiKey {
  /** Auto-generated document ID */
  id: string;
  
  /** SHA-256 hash of the full key */
  keyHash: string;
  
  /** First part for identification (e.g., "hb_live_") */
  keyPrefix: string;
  
  /** Last 4 characters for identification */
  keySuffix: string;
  
  /** Key type */
  type: ApiKeyType;
  
  /** Current status */
  status: ApiKeyStatus;
  
  /** User-provided description/label */
  label: string;
  
  /** When the key was created */
  createdAt: Timestamp;
  
  /** User ID of who created it */
  createdBy: string;
  
  /** When the key was revoked (if revoked) */
  revokedAt?: Timestamp;
  
  /** User ID of who revoked it (if revoked) */
  revokedBy?: string;
  
  /** When the key was last used */
  lastUsedAt?: Timestamp;
  
  /** Which APIs this key can access */
  enabledApis: string[];
  
  /** Custom rate limits (overrides plan defaults if set) */
  rateLimits?: {
    requestsPerMinute?: number;
    requestsPerDay?: number;
    requestsPerMonth?: number;
  };
}

/**
 * Data required to create a new API key
 */
export interface CreateApiKeyData {
  type: ApiKeyType;
  label: string;
  enabledApis: string[];
}

// =============================================================================
// Usage Types
// =============================================================================

/**
 * Usage period type
 */
export type UsagePeriodType = 'hour' | 'day' | 'month';

/**
 * Usage record document.
 * Path: /organizations/{orgId}/usage/{periodId}
 */
export interface UsageRecord {
  /** Period identifier (e.g., "2025-11", "2025-11-30") */
  period: string;
  
  /** Type of period */
  periodType: UsagePeriodType;
  
  /** Total requests in this period */
  totalRequests: number;
  
  /** Successful requests */
  successfulRequests: number;
  
  /** Failed requests */
  errorRequests: number;
  
  /** Breakdown by API */
  byApi: {
    [apiName: string]: {
      requests: number;
      errors: number;
    };
  };
  
  /** Breakdown by key */
  byKey: {
    [keyId: string]: {
      requests: number;
      errors: number;
    };
  };
  
  /** When this record was last updated */
  updatedAt: Timestamp;
}

// =============================================================================
// User Types
// =============================================================================

/**
 * User profile document.
 * Path: /users/{userId}
 */
export interface User {
  /** User ID (matches Firebase Auth UID and document ID) */
  id: string;

  /** User's email address */
  email: string;

  /** First name */
  firstName?: string;

  /** Last name */
  lastName?: string;

  /** @deprecated Use firstName + lastName. Kept for backwards compatibility. */
  displayName?: string;

  /** Profile photo URL */
  photoUrl?: string;
  
  /** Phone number (for verification bonus) */
  phoneNumber?: string;
  
  /** Whether phone is verified */
  phoneVerified?: boolean;
  
  /** Auth providers linked to this account */
  providers: string[];
  
  /** Organization memberships (denormalized for quick lookups) */
  orgMemberships: {
    orgId: string;
    orgName: string;
    role: OrgRole;
  }[];
  
  /** Default organization ID (for UI) */
  defaultOrgId?: string;
  
  /** When the user was created */
  createdAt: Timestamp;
  
  /** When the user was last updated */
  updatedAt: Timestamp;

  /** When the user last logged in */
  lastLoginAt?: Timestamp;

  /** When the user completed onboarding */
  onboardingCompletedAt?: Timestamp;

  /** How the user signed up */
  signupSource?: 'direct' | 'invitation';
}

// =============================================================================
// Invitation Types
// =============================================================================

/**
 * Invitation status
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'cancelled';

/**
 * Team invitation document.
 * Path: /organizations/{orgId}/invitations/{inviteId}
 */
export interface Invitation {
  /** Auto-generated document ID */
  id: string;

  /** Unique token for accept link (UUID) */
  token: string;

  /** Email address of the invitee */
  email: string;

  /** Role they'll have when they join */
  role: OrgRole;

  /** Current status */
  status: InvitationStatus;

  /** User ID of who sent the invitation */
  invitedBy: string;

  /** When the invitation was sent */
  createdAt: Timestamp;

  /** When the invitation expires (7 days from creation) */
  expiresAt: Timestamp;

  /** When the invitation was accepted (if accepted) */
  acceptedAt?: Timestamp;
}

// =============================================================================
// Billing Event Types (for audit trail)
// =============================================================================

/**
 * Billing event types
 */
export type BillingEventType = 
  | 'subscription_created'
  | 'subscription_updated'
  | 'subscription_cancelled'
  | 'bucket_upgraded'
  | 'bucket_downgraded'
  | 'tier_upgraded'
  | 'payment_succeeded'
  | 'payment_failed'
  | 'grace_period_started'
  | 'grace_period_ended'
  | 'free_tier_exhausted';

/**
 * Billing event document for audit trail
 * Path: /organizations/{orgId}/billing_events/{eventId}
 */
export interface BillingEvent {
  /** Auto-generated document ID */
  id: string;
  
  /** Event type */
  type: BillingEventType;
  
  /** Event timestamp */
  timestamp: Timestamp;
  
  /** Previous state */
  previousState?: {
    planId?: PlanId;
    bucketMax?: number;
    priceCents?: number;
  };
  
  /** New state */
  newState?: {
    planId?: PlanId;
    bucketMax?: number;
    priceCents?: number;
  };
  
  /** Stripe event ID (if triggered by Stripe) */
  stripeEventId?: string;
  
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

// =============================================================================
// Default Organization Values
// =============================================================================

/**
 * Default values for a new free-tier organization
 */
export const DEFAULT_ORG_VALUES: Partial<Organization> = {
  planId: 'free',
  usageThisPeriod: 0,
  lifetimeUsage: 0,
  lifetimeBonusEarned: 0,
  isRateLimited: false,
  isHardStopped: false,
};

// =============================================================================
// Plan Definitions (for backward compatibility)
// =============================================================================

/**
 * Default plan definitions for UI display
 * Used by BillingPage and KeysPage
 */
export const DEFAULT_PLANS = {
  free: {
    id: 'free' as PlanId,
    name: 'Free',
    description: 'For trying out the platform',
    priceMonthly: 0,
    features: {
      maxApiKeys: 1,
      maxTeamMembers: 1,
      rateLimits: {
        requestsPerMonth: 2000,
        requestsPerMinute: 10,
      },
    },
  },
  starter: {
    id: 'starter' as PlanId,
    name: 'Starter',
    description: 'For individual developers & small projects',
    priceMonthly: 599, // $5.99 base
    features: {
      maxApiKeys: 5,
      maxTeamMembers: 3,
      rateLimits: {
        requestsPerMonth: 50000,
        requestsPerMinute: 50,
      },
    },
  },
  pro: {
    id: 'pro' as PlanId,
    name: 'Pro',
    description: 'For growing applications & startups',
    priceMonthly: 1299, // $12.99 base
    features: {
      maxApiKeys: 20,
      maxTeamMembers: 10,
      rateLimits: {
        requestsPerMonth: 250000,
        requestsPerMinute: 100,
      },
    },
  },
  elite: {
    id: 'elite' as PlanId,
    name: 'Elite',
    description: 'For high-volume production workloads',
    priceMonthly: 3199, // $31.99 base
    features: {
      maxApiKeys: 50,
      maxTeamMembers: 25,
      rateLimits: {
        requestsPerMonth: 2000000,
        requestsPerMinute: 500,
      },
    },
  },
  enterprise: {
    id: 'enterprise' as PlanId,
    name: 'Enterprise',
    description: 'For large organizations with custom needs',
    priceMonthly: 9999, // Custom
    features: {
      maxApiKeys: -1, // Unlimited
      maxTeamMembers: -1, // Unlimited
      rateLimits: {
        requestsPerMonth: -1, // Unlimited
        requestsPerMinute: -1, // Custom
      },
    },
  },
} as const;